import { unwrapResult } from "@reduxjs/toolkit";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useGtmPopup from "@/app/common-hooks/gtm/use-gtm-popup";
import useGtmQuestionSet from "@/app/common-hooks/gtm/use-gtm-question-set";
import { TripDetails } from "@/app/common-types/quote/trip-details";
import selectLocationHookFlash from "@/app/common-utils/common.utils";
import { Routes } from "@/app/config/page-view-constants";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import {
  QuestionFour,
  QuestionSix,
  RenewalPage,
  TravellerEdit,
} from "@/config/app-constant";
import { APPLICATION, PRODUCT } from "@/constants";
import {
  saveQuestionDataBFF,
  sessionCheck,
} from "@/store/slices/quote/quote.action";
import { setQuestionData } from "@/store/slices/quote/quote.slice";
import { RootState, store } from "@/store/store";
import { QuotePayload } from "@/types/quote/quote-data";

import { useCoverForContext } from "../provider/cover-for.provider";
import { AvailbleOptionsType } from "../types/cover-for.type";

function useCoverForHook() {
  const dispatch = useDispatch<typeof store.dispatch>();
  const quoteResponse = useSelector((state: RootState) => state.quote);
  const [isFooterLoading, setIsFooterLoading] = useState(false);
  const { loading } = quoteResponse;

  const router = useRouter();
  const searchParams = useSearchParams();
  const editPage = searchParams.get(TravellerEdit);

  const { current } = useWizard();

  const [payload, setPayload] = useState<QuotePayload | null>(null);

  const {
    selectedAnswer,
    setSelectedAnswer,
    setIsOpenUnsavedChangesModal,
    isFamilyModalOpen,
  } = useCoverForContext();

  useEffect(() => {
    setSelectedAnswer(
      (quoteResponse?.attributes?.tripDetails
        ?.partyType as AvailbleOptionsType) ?? "DEFAULT"
    );
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [quoteResponse]);

  const handleSubmitButton = async () => {
    if (selectedAnswer === "DEFAULT") return;
    try {
      setIsFooterLoading(true);
      const payloadData: QuotePayload = {
        attributes: {
          ...quoteResponse.attributes,
          tripDetails: {
            ...quoteResponse.attributes.tripDetails,
            partyType: selectedAnswer,
          },
        },
      };
      setPayload(payloadData);
      dispatch(setQuestionData(payloadData));
      if (!quoteResponse?.referenceId) {
        const questionSaveData = {
          type: "IntermediateData",
          channel: quoteResponse?.channel,
          ...payloadData,
        };
        const questionData = dispatch(
          saveQuestionDataBFF({
            payload: { ...questionSaveData, viewPage: Routes.CoverFor },
            dispatch,
            router,
          })
        );
        const originalPromiseResult = unwrapResult(await questionData);
        if (originalPromiseResult) {
          router.push(current.next ?? "");
        }
      } else {
        dispatch(sessionCheck({ dispatch, router }));
        let path = current.next ?? "";
        if (editPage) {
          path = current.edges.editFlowNext ?? "";
        }
        router.push(path);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error saving question data:", error);
      setIsFooterLoading(false);
    }
  };

  const handleBackButton = () => {
    if (APPLICATION === RenewalPage) {
      if (editPage || (PRODUCT === "avanti" && APPLICATION === RenewalPage)) {
        setIsOpenUnsavedChangesModal(true);
      } else {
        router.push(current.prev ?? "");
      }
    } else {
      router.push(current.prev ?? "");
    }
  };

  // gtm

  const toLocationData = selectLocationHookFlash(
    payload?.attributes?.tripDetails as TripDetails
  );

  useGtmQuestionSet({
    pageTitle: QuestionSix,
    tripType: payload?.attributes?.tripDetails?.policyType,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
    destination: toLocationData.destination,
    destinationAdditions: toLocationData.destinationAdditions,
    tripExtra: payload?.attributes?.tripDetails?.goingOnCruise
      ? QuestionFour
      : undefined,
    coverStartDate: payload?.attributes?.tripDetails?.startDate ?? undefined,
    coverEndDate: payload?.attributes?.tripDetails?.endDate ?? undefined,
    cruise: payload?.attributes?.tripDetails?.goingOnCruise ?? undefined,
    travelFrom: payload?.attributes?.tripDetails?.fromLocation ?? undefined,
    travelGroupType: payload?.attributes?.tripDetails?.partyType ?? undefined,
    isSubmitBtnClicked: payload !== null,
  });

  useGtmPopup({
    tripType: QuestionSix,
    isPopupOpen: isFamilyModalOpen,
    popupName: "Family Travel Popup",
  });

  return {
    loading,
    isHideFooterBack:
      (APPLICATION !== RenewalPage && !!editPage) ||
      (APPLICATION === RenewalPage && !editPage && PRODUCT === "avanti"),
    isFooterLoading,
    handleSubmitButton,
    handleBackButton,
  };
}

export default useCoverForHook;
